import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, Loader2, KeyRound } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { forgot } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await forgot(email);
    setLoading(false);
    setSent(true);
  };

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 md:px-10 py-8 sm:py-14">
      <button
        onClick={() => navigate("/login")}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Sign in
      </button>

      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          Forgot password
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">
          {sent ? "Check your inbox." : "Reset your password."}
        </h1>
      </div>

      {sent ? (
        <div data-testid="forgot-sent" className="rounded-md border border-border bg-white p-6">
          <p className="text-sm text-foreground/80">
            If an account exists for <strong>{email}</strong>, we sent a reset link. Follow the link in the email to set a new password.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6 rounded-md border border-border bg-white p-6 md:p-8">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">Email</Label>
            <Input
              data-testid="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="h-11 rounded-md"
              required
            />
          </div>
          <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full rounded-md h-11 gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send reset link</>}
          </Button>
        </form>
      )}
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { reset } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await reset(token, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Password updated");
      navigate("/login");
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 md:px-10 py-8 sm:py-14">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
          Reset password
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">
          Pick a new password.
        </h1>
      </div>

      {!token ? (
        <div className="rounded-md border border-border bg-white p-6 text-sm">
          Invalid link. Go back to <Link to="/forgot-password" className="font-semibold hover:text-primary">Forgot password</Link>.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6 rounded-md border border-border bg-white p-6 md:p-8">
          <div className="space-y-2">
            <Label className="text-xs tracking-[0.15em] uppercase font-semibold">New password</Label>
            <Input
              data-testid="reset-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 rounded-md"
              required
              minLength={6}
            />
          </div>
          {error && (
            <div data-testid="reset-error" className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
          <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full rounded-md h-11 gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4" /> Update password</>}
          </Button>
        </form>
      )}
    </div>
  );
}
